import React from "react";
import TruncatedText from "Components/Common/TruncatedText";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import { ICFRecord } from "./StudyICFAttachmentModal";
import { CheckListQuestion } from "../../../modules/Library/Checklist/helpers/checklist.types";
import { PrimaryLanguage_OPTIONS, getLabelFromValue } from "api/modules/common.option";
import styles from "../../../modules/Volunteer/AssigningStudyModule/StudyICF/css/studyicf.module.scss";
import { formatDate } from "common/service/CommonService";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  tempICFRecord: ICFRecord | null;
  icfQuestions: CheckListQuestion[];
  pendingFormData: Record<string, string> | null;
  onConfirm: () => void;
  isloading: boolean;
}

const StudyICFSubmissionModal: React.FC<Props> = ({
  isOpen,
  toggle,
  tempICFRecord,
  icfQuestions,
  pendingFormData,
  onConfirm,
  isloading
}) => {
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader className="p-3 border-bottom" toggle={toggle}>
        Final Submission Review
      </ModalHeader>

      <ModalBody className="p-3">
        <Row className="gy-3">
          {/* LEFT COLUMN: DOCUMENT SUMMARY */}
          <Col lg={5}>
            <div className="h-100 p-2 px-3 bg-light-subtle rounded-4 border border-light shadow-sm">
              <h6 className="fw-bold text-primary mb-2 fs-12 text-uppercase d-flex align-items-center gap-2">
                <i className="ri-file-info-line fs-14"></i> Document Details
              </h6>
              <div className="d-flex flex-column gap-2">
                <div className="bg-white p-1 px-3 rounded-3 border border-light shadow-none">
                  <span
                    className="text-muted d-block"
                    style={{
                      fontSize: "9px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    ICF Version
                  </span>
                  <span className="fw-bold text-dark fs-13">
                    {tempICFRecord?.version || "N/A"}
                  </span>
                </div>
                <div className="bg-white p-1 px-3 rounded-3 border border-light shadow-none">
                  <span
                    className="text-muted d-block"
                    style={{
                      fontSize: "9px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Language
                  </span>
                  <span className="fw-bold text-dark fs-13">
                    {tempICFRecord?.language
                      ? isNaN(Number(tempICFRecord.language))
                        ? tempICFRecord.language
                        : getLabelFromValue(PrimaryLanguage_OPTIONS, tempICFRecord.language, "English")
                      : "N/A"}
                  </span>
                </div>
                <div className="bg-white p-1 px-3 rounded-3 border border-light">
                  <span
                    className="text-muted d-block"
                    style={{
                      fontSize: "9px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Signed Date
                  </span>
                  <span className="fw-bold text-dark fs-13">
                    {tempICFRecord?.signeddate
                      ? formatDate(tempICFRecord.signeddate)
                      : "N/A"}
                  </span>
                </div>
                <div className="mt-1 p-2 rounded-3 border border-dashed text-center bg-info-subtle border-info text-info">
                  <i className="ri-file-text-line fs-5 d-block mb-1"></i>
                  <span
                    className="text-muted d-block"
                    style={{ fontSize: "9px", textTransform: "uppercase" }}
                  >
                    Attached File
                  </span>
                  <span className="fw-bold fs-12 text-dark d-block">
                    <TruncatedText text={tempICFRecord?.filename || "N/A"} />
                  </span>
                </div>
              </div>
            </div>
          </Col>

          {/* RIGHT COLUMN: QUESTIONNAIRE SUMMARY */}
          <Col lg={7}>
            <div className="h-100 p-2 px-3 bg-white border border-light rounded-4 shadow-sm">
              <h6 className="fw-bold text-dark mb-2 fs-12 text-uppercase d-flex align-items-center gap-2">
                <i className="ri-list-check-2 fs-14 text-primary"></i>{" "}
                Understanding Evaluation
              </h6>
              <SimpleBar style={{ height: "240px" }} className="pe-2 mt-2">
                <div className="d-flex flex-column gap-2">
                  {icfQuestions.map((q) => {
                    const answerVal = pendingFormData?.[String(q.masterchecklistid)];
                    const isYes = String(answerVal) === "1";
                    const isNo = String(answerVal) === "0";
                    const hasAnswer = isYes || isNo;

                    return (
                      <div
                        key={q.masterchecklistid}
                        className="p-1 px-3 border border-light rounded-3 d-flex justify-content-between align-items-center bg-light-subtle transition-all hover-shadow"
                      >
                        <span
                          className="text-muted fs-11 fw-medium text-truncate pe-2"
                          title={q.question}
                        >
                          {q.question}
                        </span>
                        {hasAnswer ? (
                          <Badge
                            color={isYes ? "primary" : "secondary"}
                            className={`rounded-pill shadow-sm transition-all ${isYes ? "bg-primary" : "bg-dark"
                              }`}
                            style={{
                              fontSize: "10px",
                              minWidth: "50px",
                              textAlign: "center",
                              display: "inline-block"
                            }}
                          >
                            {isYes ? "Yes" : "No"}
                          </Badge>
                        ) : !q.isrequired ? (
                          <Badge
                            className={`rounded-pill shadow-sm ${styles.na_badge}`}
                            style={{
                              fontSize: "10px",
                              minWidth: "50px",
                              textAlign: "center",
                              display: "inline-block"
                            }}
                          >
                            N/A
                          </Badge>
                        ) : (
                          <span className="text-danger small fw-bold">
                            Pending
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SimpleBar>
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12} className="text-center">
            <p className="text-danger fw-bold mb-0 fs-13">
              {/* <i className="ri-error-warning-line me-1"></i> */}
              Please confirm the details above. This submission will be final and cannot be edited.
            </p>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter>
        <Button
          outline
          color="light"
          onClick={toggle}
        >
          Cancel
        </Button>
        <Button type="submit" color="primary" disabled={isloading} onClick={onConfirm}>
          {isloading && <Spinner size="sm" className="me-2" />}
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default StudyICFSubmissionModal;
